// What an order line's OneBill plan needs beyond what Omni fills itself (#1732). OneBill
// price plans mark some features mandatory. Omni fills the RPP Account ID and the Service
// Name on every line; Phoneware ops enter the rest at billing approval. The seller enters
// each line's Telephone Number on the order (#1763), and ops confirm it: the approval sheet
// starts from the seller's number, else the customer's main number from Autotask.

/** The mandatory OneBill plan features Omni fills on every order line, so ops never enter them. */
export const ONEBILL_FEATURES_OMNI_FILLS: readonly string[] = ["RPP Account ID", "Service Name"];

/** The OneBill plan feature most catalog plans make mandatory. */
export const TELEPHONE_NUMBER_FEATURE = "Telephone Number";

/** Where a Telephone Number on the approval sheet starts (#1763): the number the seller set on the line, or the customer's main number. */
export type BillingPrefillSource = "seller" | "main_phone";

export interface BillingLineFeature {
  featureName: string;
  /** What the approval sheet starts with: for a Telephone Number the seller's number, else the customer's main number; otherwise empty. */
  defaultValue: string;
  /** Which of the two `defaultValue` is, or null when it starts empty. */
  prefilledFrom: BillingPrefillSource | null;
}

export interface BillingLineRequirement {
  /** The line's position on the signed quote, from 0. OneBill's subscription identifier counts the same lines from 1. */
  line: number;
  productId: string;
  name: string;
  pricePlanId: string;
  /** The mandatory features on the line's plan that Omni doesn't fill. Never empty. */
  features: BillingLineFeature[];
}

/** GET /api/orders/:id/billing-requirements: what ops enter before billing is approved. Anyone who can see the order reads it. */
export interface BillingRequirements {
  orderId: string;
  /** The customer's main phone in Autotask as 10 digits, or null when it has none that is. */
  mainPhone: string | null;
  /** Only the lines that need something. An order whose lines need nothing has none. */
  lines: BillingLineRequirement[];
}

/** One value ops entered for one line's feature: sent with the approval, stored on the order, given to OneBill. */
export interface BillingLineValue {
  line: number;
  featureName: string;
  value: string;
}

/** The mandatory features ops enter: everything mandatory except what Omni fills, each once, in the plan's order. */
export function featuresOpsEnter(mandatory: readonly string[]): string[] {
  const out: string[] = [];
  for (const name of mandatory) {
    if (ONEBILL_FEATURES_OMNI_FILLS.includes(name) || out.includes(name)) continue;
    out.push(name);
  }
  return out;
}

/**
 * A US telephone number as its 10 digits, or null. Spaces, dots, dashes, parentheses and a leading +1 or 1 are allowed
 * around the digits; letters (an extension) are not.
 */
export function telephoneDigits(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text || /[^0-9\s().+-]/.test(text)) return null;
  let digits = text.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.length === 10 ? digits : null;
}

/** What is wrong with a value ops typed for a feature, in the words the sheet shows under the field, or null when it's good. */
export function lineValueProblem(featureName: string, raw: string): string | null {
  if (!raw.trim()) return `Enter the ${featureName}.`;
  if (featureName === TELEPHONE_NUMBER_FEATURE && telephoneDigits(raw) === null) return "A Telephone Number is 10 digits.";
  return null;
}

/** The value as OneBill gets it: a Telephone Number as its 10 digits, anything else trimmed. */
export function normalizeLineValue(featureName: string, raw: string): string {
  if (featureName === TELEPHONE_NUMBER_FEATURE) return telephoneDigits(raw) ?? raw.trim();
  return raw.trim();
}

export type LineValuesCheck =
  | { ok: true; values: BillingLineValue[] }
  | { ok: false; error: string; line: number | null; featureName: string | null };

const SHAPE = "lineValues must be a list of { line, featureName, value }.";

function isLineValue(v: unknown): v is BillingLineValue {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.line === "number" && Number.isInteger(o.line) && o.line >= 0 && typeof o.featureName === "string" && typeof o.value === "string";
}

/**
 * Checks the values an approval carries against what the order's lines need. Every required feature needs a value, and
 * a Telephone Number needs 10 digits. The good values come back normalized, one per required feature; values for
 * features nothing requires are dropped.
 */
export function checkBillingLineValues(lines: readonly BillingLineRequirement[], raw: unknown): LineValuesCheck {
  if (raw !== undefined && raw !== null && !Array.isArray(raw)) return { ok: false, error: SHAPE, line: null, featureName: null };
  const given: unknown[] = Array.isArray(raw) ? raw : [];
  if (!given.every(isLineValue)) return { ok: false, error: SHAPE, line: null, featureName: null };
  const values: BillingLineValue[] = [];
  for (const req of lines) {
    for (const f of req.features) {
      const found = (given as BillingLineValue[]).find((g) => g.line === req.line && g.featureName === f.featureName);
      const value = found?.value ?? "";
      const label = `Line ${req.line + 1}, ${req.name}`;
      if (!value.trim()) {
        return { ok: false, error: `${label}, needs its ${f.featureName}.`, line: req.line, featureName: f.featureName };
      }
      if (f.featureName === TELEPHONE_NUMBER_FEATURE && telephoneDigits(value) === null) {
        return { ok: false, error: `${label}: a Telephone Number is 10 digits.`, line: req.line, featureName: f.featureName };
      }
      values.push({ line: req.line, featureName: f.featureName, value: normalizeLineValue(f.featureName, value) });
    }
  }
  return { ok: true, values };
}

/** One line's Telephone Number as the seller set it on the order (#1763). Phoneware confirms it at billing approval. */
export interface OrderLineNumber {
  /** The line's position on the signed quote, from 0, as billing requirements count it. */
  line: number;
  /** 10 digits. */
  number: string;
}

/** What a line's Telephone Number starts as on the approval sheet: the seller's number for the line, else the main number. */
export function telephonePrefill(
  sellerNumber: string | null | undefined,
  mainPhone: string | null | undefined,
): { defaultValue: string; prefilledFrom: BillingPrefillSource | null } {
  const seller = telephoneDigits(sellerNumber);
  if (seller) return { defaultValue: seller, prefilledFrom: "seller" };
  const main = telephoneDigits(mainPhone);
  if (main) return { defaultValue: main, prefilledFrom: "main_phone" };
  return { defaultValue: "", prefilledFrom: null };
}

/** The number the seller set on one line, or null. */
export function sellerLineNumber(numbers: readonly OrderLineNumber[] | null | undefined, line: number): string | null {
  return (numbers ?? []).find((n) => n.line === line)?.number ?? null;
}

export type LineNumbersCheck =
  | { ok: true; changes: Array<{ line: number; number: string | null }> }
  | { ok: false; error: string; line: number | null };

const LINE_NUMBERS_SHAPE = "lineNumbers must be a list of { line, number }.";

function isLineNumber(v: unknown): v is OrderLineNumber {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.line === "number" && Number.isInteger(o.line) && o.line >= 0 && typeof o.number === "string";
}

/**
 * Checks the Telephone Numbers a seller sets on an order's lines (#1763). A number goes only on a line whose OneBill plan
 * needs one, once, as 10 digits; an empty one takes the line's number off. `lineNames` are the signed quote's lines, by
 * position. The good ones come back as changes, a number as its 10 digits or null to take one off.
 */
export function checkLineNumbers(required: readonly BillingLineRequirement[], lineNames: readonly string[], raw: unknown): LineNumbersCheck {
  if (!Array.isArray(raw) || !raw.every(isLineNumber)) return { ok: false, error: LINE_NUMBERS_SHAPE, line: null };
  const changes: Array<{ line: number; number: string | null }> = [];
  for (const given of raw) {
    const name = lineNames[given.line];
    if (name === undefined) return { ok: false, error: `This order has no line ${given.line + 1}.`, line: given.line };
    const label = `Line ${given.line + 1}, ${name}`;
    const needs = required.find((r) => r.line === given.line)?.features.some((f) => f.featureName === TELEPHONE_NUMBER_FEATURE) ?? false;
    if (!needs) return { ok: false, error: `${label}, doesn't take a Telephone Number.`, line: given.line };
    if (changes.some((c) => c.line === given.line)) return { ok: false, error: `${label}, is listed twice.`, line: given.line };
    if (!given.number.trim()) {
      changes.push({ line: given.line, number: null });
      continue;
    }
    const digits = telephoneDigits(given.number);
    if (digits === null) return { ok: false, error: `${label}: a Telephone Number is 10 digits.`, line: given.line };
    changes.push({ line: given.line, number: digits });
  }
  return { ok: true, changes };
}

/** The order's line numbers after a seller's changes, one per line, in line order. */
export function mergeLineNumbers(
  existing: readonly OrderLineNumber[] | null | undefined,
  changes: ReadonlyArray<{ line: number; number: string | null }>,
): OrderLineNumber[] {
  const byLine = new Map((existing ?? []).map((n) => [n.line, n.number]));
  for (const c of changes) {
    if (c.number === null) byLine.delete(c.line);
    else byLine.set(c.line, c.number);
  }
  return [...byLine.entries()].sort(([a], [b]) => a - b).map(([line, number]) => ({ line, number }));
}

/** The values ops entered for one line, as OneBill order element attributes. */
export function attributesForLine(values: readonly BillingLineValue[] | null | undefined, line: number): Array<{ featureName: string; value: string }> {
  return (values ?? []).filter((v) => v.line === line).map((v) => ({ featureName: v.featureName, value: v.value }));
}
